import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import I18n from 'i18n-js';
import { connect } from 'react-redux';
import {
  bulkUploadDeliveriesSelector,
  bulkUploadDeliveryIDsSelector,
  bulkUploadOfferConfigSelector,
} from 'selectors/new-bulk-upload-selectors';
import { Form } from 'grab-ui';
import InputField from 'components/v2/input-field/input-field';
import { showEnterpriseTripInputSelector } from 'selectors/app-selector';
import { deliveryFormFields } from 'views/v2/new-booking-page/delivery-form/common';
import PaymentMethodSelector from 'components/v2/anytime-booking/bulk-booking/payment-method-selector';
import _sumBy from 'lodash/sumBy';
import _get from 'lodash/get';
import _every from 'lodash/every';
import { NewBookingDeliveryPropType } from 'common/prop-types';
import calcPrice from 'common/utils/calc-price';
import NewBookingDuck from 'ducks/new-booking-duck';
import { deliveryPriceReady } from 'common/anytime-booking/delivery';
import scribeNewBulkUploadDuck from 'ducks/scribe-new-bulk-upload-duck';
import RefreshFailedQuotesButton from '../common/RefreshFailedQuotesButton';

import { PayAndBookContext } from '../common/context';
import OfferSelector from './offer-selector';
import PriceSection from './price-section';
import styles from './pay-and-book.less';

const PayAndBook = ({
  dispatch,
  deliveryIDs,
  deliveries,
  offerConfig,
  form,
  showEnterpriseTripInput,
}) => {
  const payAndBookContext = useMemo(
    () => {
      const currency = _get(deliveries[0], 'quote.currency');
      return {
        currency,
        code: _get(currency, 'code'),
        totalDiscount: calcPrice(
          _sumBy(deliveries, delivery =>
            _get(delivery, 'quote.discount.discountAmount', 0),
          ),
          currency,
        ),
        totalOriginalFare: calcPrice(
          _sumBy(deliveries, delivery =>
            _get(delivery, 'quote.originalFare.lowerBound', 0),
          ),
          currency,
        ),
        totalFinalFare: calcPrice(
          _sumBy(deliveries, delivery =>
            _get(delivery, 'quote.finalFare.lowerBound', 0),
          ),
          currency,
        ),
        offerConfig,
      };
    },
    [deliveries, offerConfig],
  );

  useEffect(
    () => {
      // run once
      const { totalFinalFare, code } = payAndBookContext;
      dispatch(
        scribeNewBulkUploadDuck.creators.scribeExpressBukUploadBookDefault(
          totalFinalFare,
          code,
          deliveries.length,
        ),
      );
    },
    [dispatch],
  );

  const tripCode = useMemo(() => _get(deliveries[0], 'detail.tripCode', ''), [
    deliveries,
  ]);

  const tripDescription = useMemo(
    () => _get(deliveries[0], 'detail.tripDescription', ''),
    [deliveries],
  );

  const refreshFailedQuotesButton = useMemo(
    () => {
      const isAllQuotesReady = _every(deliveries, deliveryPriceReady);
      if (isAllQuotesReady) {
        return null;
      }

      return (
        <div className={styles.refreshFailedQuotesButton}>
          <RefreshFailedQuotesButton deliveries={deliveries} />
        </div>
      );
    },
    [deliveries],
  );

  return (
    <PayAndBookContext.Provider value={payAndBookContext}>
      <div className={styles.payAndBook}>
        <div className={styles.leftBlocks}>
          <div className={styles.section}>
            {refreshFailedQuotesButton}
            <h2 className={styles.sectionTitle}>
              {I18n.t(
                'phrase.geWebBulkUpload.express_web_bulk_upload_heading_payment_method_happypath',
              )}
            </h2>
            <PaymentMethodSelector deliveryIDs={deliveryIDs} />
          </div>
          {showEnterpriseTripInput && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>
                {I18n.t('v2.homepage.manualOrder.heading.paymentDetails')}
              </h2>
              <div className={styles.tripInputWrapper}>
                <InputField
                  id={deliveryFormFields.tripCode.fieldName}
                  fieldClassName={styles.tripInput}
                  placeholder={I18n.t(
                    'v2.homepage.manualOrder.placeholder.tripCode',
                  )}
                  fieldOptions={{
                    initialValue: tripCode,
                    rules: deliveryFormFields.tripCode.makeRules(),
                  }}
                  form={form}
                />
                <InputField
                  id={deliveryFormFields.tripDescription.fieldName}
                  fieldClassName={styles.tripInput}
                  placeholder={I18n.t(
                    'v2.homepage.manualOrder.placeholder.tripDescription',
                  )}
                  fieldOptions={{
                    initialValue: tripDescription,
                    rules: deliveryFormFields.tripDescription.makeRules(),
                  }}
                  form={form}
                />
              </div>
            </div>
          )}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              {I18n.t(
                'phrase.geWebBulkUpload.express_web_bulk_upload_heading_offers_happypath',
              )}
            </h2>
            <OfferSelector deliveryIDs={deliveryIDs} />
          </div>
        </div>
        <div className={styles.rightBlocks}>
          <PriceSection deliveryIDs={deliveryIDs} />
        </div>
      </div>
    </PayAndBookContext.Provider>
  );
};

PayAndBook.defaultProps = {
  offerConfig: null,
  showEnterpriseTripInput: false,
};

PayAndBook.propTypes = {
  deliveryIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
  deliveries: PropTypes.arrayOf(NewBookingDeliveryPropType).isRequired,
  dispatch: PropTypes.func.isRequired,
  offerConfig: PropTypes.shape({}),
  form: PropTypes.shape({}).isRequired,
  showEnterpriseTripInput: PropTypes.bool,
};

const onFieldsChange = ({ dispatch, deliveryIDs }, changedFields, _) => {
  Object.entries(changedFields).forEach(([fn, fv]) => {
    dispatch(
      NewBookingDuck.creators.bulkUpdateDeliveryDetail(deliveryIDs, {
        [fn]: fv.value,
      }),
    );
  });
};

export default connect(state => ({
  deliveryIDs: bulkUploadDeliveryIDsSelector(state),
  deliveries: bulkUploadDeliveriesSelector(state),
  offerConfig: bulkUploadOfferConfigSelector(state),
  showEnterpriseTripInput: showEnterpriseTripInputSelector(state),
}))(Form.create({ onFieldsChange })(PayAndBook));
